/**
 * Terminal 82 - Card Renderer
 *
 * Main renderer that assembles all components into a complete card.
 */

import type { DayData, CardConfig } from '../types';
import { getDisplayLabel } from '../types';
import { getThemeClasses } from '../themes';
import { createCandle3D } from './candle';
import { createIntradayChart } from './intraday';
import {
  formatDisplayDate,
  formatPrice,
  formatOHLCPrice,
  formatPercentChange,
  generateYAxisLabels,
} from './utils';
import type { CardTraits } from '../stats';

// ============================================
// Card Section Builders
// ============================================

function renderHeader(data: DayData): string {
  return `
    <div class="header">
      <span class="header-logo">TERMINAL 82</span>
      <span class="header-date">${formatDisplayDate(data.date)}</span>
    </div>
  `;
}

function renderBadgeBar(config: CardConfig): string {
  // Determine badge class based on tier or mythic event
  let badgeClass = `badge-${config.tier}`;
  if (config.mythicEvent) {
    badgeClass = `badge-${config.mythicEvent}`;
  }

  // Get display label using the shared helper
  const displayLabel = getDisplayLabel({
    tier: config.tier,
    theme: config.theme,
    direction: config.direction,
    mythicEvent: config.mythicEvent
  });

  return `
    <div class="badge-bar">
      <span class="badge ${badgeClass}">${displayLabel}</span>
    </div>
  `;
}

function renderChartSection(data: DayData, config: CardConfig): string {
  const yLabels = generateYAxisLabels(data.ohlc);

  const candle = createCandle3D({
    ohlc: data.ohlc,
    changePercent: data.change.percent,
    tier: config.tier,
  });

  // Pass real data only - intraday chart handles "no data" state
  const intradayChart = createIntradayChart({
    prices: data.intraday,
    volumes: data.intradayVolumes,
    direction: config.direction,
  });

  return `
    <div class="chart-section">
      <div class="chart-area">
        <div class="y-axis">
          ${yLabels.map((label) => `<span>${label}</span>`).join('\n          ')}
        </div>
        <div class="chart-grid">
          ${candle}
        </div>
      </div>
      ${intradayChart}
    </div>
  `;
}

function renderSignature(data: DayData, config: CardConfig): string {
  const volumeId = config.volumeId || 'V1';
  const serialNumber = `${volumeId}-${String(data.dayId).padStart(5, '0')}`;
  const edition = config.edition || '1/1';

  return `
    <div class="card-signature">
      <span class="signature-edition">${edition}</span>
      <span class="signature-serial">${serialNumber}</span>
    </div>
  `;
}

function renderDataPanel(data: DayData, _config: CardConfig, traits?: CardTraits): string {
  const changeClass = data.change.percent >= 0 ? 'positive' : 'negative';

  return `
    <div class="data-panel">
      <div class="data-box">
        <div class="data-box-header">ETH/USD</div>
        <div class="data-box-content">
          <div class="price-main">
            <span class="price-value">${formatPrice(data.ohlc.close)}</span>
            <div class="price-change ${changeClass}">${formatPercentChange(data.change.percent)}</div>
          </div>
        </div>
      </div>

      <div class="data-box">
        <div class="data-box-header">OHLC</div>
        <div class="data-box-content">
          <div class="ohlc-grid">
            <div class="ohlc-item">
              <span class="ohlc-label">O</span>
              <span class="ohlc-value">${formatOHLCPrice(data.ohlc.open)}</span>
            </div>
            <div class="ohlc-item">
              <span class="ohlc-label">H</span>
              <span class="ohlc-value">${formatOHLCPrice(data.ohlc.high)}</span>
            </div>
            <div class="ohlc-item">
              <span class="ohlc-label">L</span>
              <span class="ohlc-value">${formatOHLCPrice(data.ohlc.low)}</span>
            </div>
            <div class="ohlc-item">
              <span class="ohlc-label">C</span>
              <span class="ohlc-value">${formatOHLCPrice(data.ohlc.close)}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="data-box">
        <div class="data-box-header">TRAITS</div>
        <div class="data-box-content market-data">
          <div class="market-row">
            <span class="market-label">MOM</span>
            <span class="market-value">${traits?.momentum || 'FLATLINE'}</span>
          </div>
          <div class="market-row">
            <span class="market-label">RNG</span>
            <span class="market-value">${traits?.volatility || 'STEADY'}</span>
          </div>
          <div class="market-row">
            <span class="market-label">RVOL</span>
            <span class="market-value">${traits?.volume || 'NORMAL'}</span>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// Main Card Renderer
// ============================================

/**
 * Build CSS class string for card
 */
function buildCardClasses(config: CardConfig): string {
  const classes = ['card', 'frame-1982', 'style-tron', config.direction];

  const themeClasses = getThemeClasses(config.theme);
  classes.push(...themeClasses);

  return classes.join(' ');
}

/**
 * Render complete card HTML
 */
export function renderCard(data: DayData, config: CardConfig, traits?: CardTraits): HTMLElement {
  const card = document.createElement('div');
  card.className = buildCardClasses(config);

  const isBlackSwan = config.mythicEvent === 'black_swan';

  // Theme class is now added via buildCardClasses from config.theme
  // No need to add special event classes since theme already maps correctly:
  // - black_swan mythicEvent → theme-black_swan
  // - supernova mythicEvent → theme-supernova
  // - genesis mythicEvent → theme-genesis

  // Apply trait classes (visual effects)
  if (config.traits && config.traits.length > 0) {
    config.traits.forEach((trait) => {
      card.classList.add(`trait-${trait}`);
    });
  }

  card.innerHTML = `
    <div class="crt-screen">
      <div class="scanlines"></div>
      <div class="vignette"></div>
      <div class="terminal-content">
        ${renderHeader(data)}
        ${renderBadgeBar(config)}
        <div class="main-content">
          ${renderChartSection(data, config)}
          ${renderDataPanel(data, config, traits)}
        </div>
        ${renderSignature(data, config)}
      </div>
      ${isBlackSwan ? '<div class="system-failure">SYSTEM FAILURE</div>' : ''}
    </div>
  `;

  return card;
}

/**
 * Render card to a container element
 */
export function renderCardTo(
  container: HTMLElement,
  data: DayData,
  config: CardConfig,
  traits?: CardTraits
): HTMLElement {
  const card = renderCard(data, config, traits);
  container.innerHTML = '';
  container.appendChild(card);
  return card;
}

/**
 * Update existing card with new data
 */
export function updateCard(
  card: HTMLElement,
  data: DayData,
  config: CardConfig,
  traits?: CardTraits
): void {
  // Update class names
  card.className = buildCardClasses(config);

  // Re-render content
  const newCard = renderCard(data, config, traits);
  card.innerHTML = newCard.innerHTML;
}
